import React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@/components/errorBoundary';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { HourlyActivity as HourlyActivityModel } from '@/api/models';
import { HourlyActivitySkeleton } from './HourlyActivitySkeleton';
import { HourlyActivityView } from './HourlyActivityView';
import { useHourlyActivity } from './useHourlyActivity';

type HourlyActivityContentProps = {
    data: HourlyActivityModel | null;
    error: ErrorResponse | null;
    isLoading: boolean;
};

/**
 * Self-served hourly-activity widget — same container / view / skeleton /
 * hook split as the other Sales widgets. `useHourlyActivity` reads the live
 * endpoint through `useHourlyActivityQuery`.
 */
export const HourlyActivity: React.FC = () => {
    const { data, error, isLoading, onRetry } = useHourlyActivity();
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            errorTitle: t('sales.widgets.hourlyActivity.errorTitle'),
            errorMessage: t('sales.widgets.hourlyActivity.errorMessage'),
            errorRetry: t('sales.widgets.hourlyActivity.errorRetry'),
        }),
        [t],
    );

    return (
        <ErrorBoundary
            data-testid="hourly-activity-error-boundary"
            fallbackMessage={copy.errorMessage}
            id="hourly-activity-error-boundary"
            onRetry={onRetry}
            resetKeys={[error]}
            retryLabel={copy.errorRetry}
            title={copy.errorTitle}
        >
            <HourlyActivityContent
                data={data}
                error={error}
                isLoading={isLoading}
            />
        </ErrorBoundary>
    );
};

const HourlyActivityContent: React.FC<HourlyActivityContentProps> = ({
    data,
    error,
    isLoading,
}) => {
    if (error) {
        throw error;
    }

    if (isLoading || data === null) {
        return <HourlyActivitySkeleton />;
    }

    return <HourlyActivityView data={data} />;
};
